/**
 * Terrain: the alphabet an authored island is written in.
 *
 * This file is deliberately pure and lives in core rather than in the renderer, because three very
 * different places need the same answer to "can a person stand here":
 *
 *   - the game, to stop the player walking into a cliff
 *   - the world builder, to draw the island the admin is painting
 *   - the publish gate, to refuse a world where a student's next step is walled off
 *
 * If those three ever disagree, the third one is the expensive kind of disagreement: content that
 * validates clean and then strands somebody. So there is one enum, one walkability rule and one
 * reachability search, and everything else imports them.
 *
 * The values 0-10 are the ones the generator has always produced and are frozen — a published
 * revision stores terrain as a string of these characters, so renumbering them would silently
 * repaint every island that was ever authored. New terrain is appended.
 */

export enum Terrain {
  Deep = 0,
  Sea = 1,
  Shallow = 2,
  Sand = 3,
  Ground = 4,
  Path = 5,
  Cliff = 6,
  Tree = 7,
  Rock = 8,
  Fog = 9,
  Dock = 10,
  /** Walkable, and the only thing that crosses water on foot. */
  Bridge = 11,
  /** Ground with something growing on it. Decorative, always walkable. */
  Flowers = 12,
  Bush = 13,
  Fence = 14,
  /** A building. Solid — you walk around it, there is no inside. */
  Hut = 15,
  TallGrass = 16,
}

export const TERRAIN_VALUES: readonly Terrain[] = [
  Terrain.Deep,
  Terrain.Sea,
  Terrain.Shallow,
  Terrain.Sand,
  Terrain.Ground,
  Terrain.Path,
  Terrain.Cliff,
  Terrain.Tree,
  Terrain.Rock,
  Terrain.Fog,
  Terrain.Dock,
  Terrain.Bridge,
  Terrain.Flowers,
  Terrain.Bush,
  Terrain.Fence,
  Terrain.Hut,
  Terrain.TallGrass,
];

/**
 * What blocks a step. Fog is in here as well as being the edge of the world: an authored island
 * can paint it, and it has to mean the same thing everywhere.
 */
export const SOLID: ReadonlySet<Terrain> = new Set([
  Terrain.Cliff,
  Terrain.Tree,
  Terrain.Rock,
  Terrain.Fog,
  Terrain.Bush,
  Terrain.Fence,
  Terrain.Hut,
]);

export const WATER: ReadonlySet<Terrain> = new Set([Terrain.Deep, Terrain.Sea, Terrain.Shallow]);

export const isSolid = (terrain: Terrain): boolean => SOLID.has(terrain);
export const isWater = (terrain: Terrain): boolean => WATER.has(terrain);

/**
 * Dry land you can put something on. Water is walkable in the game — you row across it — but a
 * signpost or a person standing in the sea is a bug, not a design, so placement uses this.
 */
export function isStandable(terrain: Terrain): boolean {
  return !isSolid(terrain) && !isWater(terrain);
}

/* ------------------------------------------------------------------ the codec */

/**
 * One character per terrain, none of them a digit — the run-length counts are digits, and the
 * decoder tells the two apart by that alone.
 *
 * These characters end up inside published JSON that outlives every one of us, so they are chosen
 * to survive a human reading the file: `~` is sea, `s` is sand, `t` is a tree.
 */
const CHARS: Record<Terrain, string> = {
  [Terrain.Deep]: '_',
  [Terrain.Sea]: '~',
  [Terrain.Shallow]: '-',
  [Terrain.Sand]: 's',
  [Terrain.Ground]: 'g',
  [Terrain.Path]: 'p',
  [Terrain.Cliff]: 'c',
  [Terrain.Tree]: 't',
  [Terrain.Rock]: 'r',
  [Terrain.Fog]: 'f',
  [Terrain.Dock]: 'd',
  [Terrain.Bridge]: 'b',
  [Terrain.Flowers]: 'w',
  [Terrain.Bush]: 'h',
  [Terrain.Fence]: 'n',
  [Terrain.Hut]: 'k',
  [Terrain.TallGrass]: 'l',
};

const BY_CHAR = new Map<string, Terrain>(
  TERRAIN_VALUES.map((terrain) => [CHARS[terrain], terrain] as const),
);

export const terrainChar = (terrain: Terrain): string => CHARS[terrain] ?? CHARS[Terrain.Sea];

/** The widest island the builder will draw. Bounded so one island can never blow the content budget. */
export const MIN_ISLAND_SIZE = 8;
export const MAX_ISLAND_SIZE = 64;

/**
 * Tiles → a run-length string: `g12` is twelve grass, a bare `g` is one.
 *
 * Painted islands are mostly big flat areas of one thing, so this is typically a 10:1 win over a
 * character per tile, and — the reason it is not base64 — a person can open mapa.json in a diff
 * and see that an island got a beach.
 */
export function encodeTerrain(tiles: ArrayLike<number>, width: number, height: number): string {
  const count = width * height;
  let out = '';
  let index = 0;
  while (index < count) {
    const value = (tiles[index] ?? Terrain.Sea) as Terrain;
    const char = CHARS[value] ?? CHARS[Terrain.Sea];
    let run = 1;
    while (index + run < count && ((tiles[index + run] ?? Terrain.Sea) as Terrain) === value) run++;
    out += run > 1 ? `${char}${run}` : char;
    index += run;
  }
  return out;
}

/**
 * A run-length string → tiles, or null if it does not describe exactly width × height of them.
 *
 * Null rather than a best effort on purpose. A terrain string that decodes short is content that
 * has been truncated somewhere, and half an island rendered as open sea would put a student's
 * next step underwater without anything anywhere saying so. The callers all treat null as "this
 * island has no authored terrain", which falls back to the generator — a wrong-looking island
 * beats a drowned one.
 */
export function decodeTerrain(text: string, width: number, height: number): Uint8Array | null {
  if (!Number.isInteger(width) || !Number.isInteger(height)) return null;
  if (width < MIN_ISLAND_SIZE || height < MIN_ISLAND_SIZE) return null;
  if (width > MAX_ISLAND_SIZE || height > MAX_ISLAND_SIZE) return null;

  const count = width * height;
  const tiles = new Uint8Array(count);
  let out = 0;
  let index = 0;

  while (index < text.length) {
    const terrain = BY_CHAR.get(text[index]!);
    if (terrain === undefined) return null;
    index += 1;

    let run = 0;
    while (index < text.length && text[index]! >= '0' && text[index]! <= '9') {
      run = run * 10 + (text.charCodeAt(index) - 48);
      // A count longer than the whole island is corrupt input, and left unchecked it is also an
      // unbounded loop counter parsed out of stored text.
      if (run > count) return null;
      index += 1;
    }
    if (run === 0) run = 1;

    if (out + run > count) return null;
    tiles.fill(terrain, out, out + run);
    out += run;
  }

  return out === count ? tiles : null;
}

/**
 * How big the generator makes an island with this many nodes.
 *
 * Lives here, in the pure layer, because two very different callers need the same number: the
 * renderer, which grows the island, and the publish gate, which has to know how much sea an island
 * takes up in order to notice two of them landing on top of each other. A second copy of this
 * formula would let those two disagree, and the disagreement would look like an island vanishing.
 */
export function generatedIslandRadius(
  nodeCount: number,
  columns = GENERATED_COLUMNS,
): { radiusX: number; radiusY: number } {
  const rows = Math.max(2, Math.ceil(nodeCount / columns));
  return {
    radiusX: Math.max(6, Math.min(4 + columns * 3, 4 + Math.ceil(nodeCount * 0.8))),
    radiusY: Math.max(5, 3 + rows * 2),
  };
}

/**
 * How many markers the generator puts in a row.
 *
 * Three is what every course island has always used and the default reproduces those islands byte
 * for byte. It is a parameter because the serpentine grows only downwards: at three across, an
 * island of twenty-two tasks is eleven rows and forty-one tiles tall — a strip several screens
 * long, and past thirty-seven nodes the builder's canvas clips its southern shore against
 * MAX_ISLAND_SIZE. A free island holding a whole exercise catalogue is wide instead.
 */
export const GENERATED_COLUMNS = 3;
export const FREE_ISLAND_COLUMNS = 6;

/**
 * Markers per row, for a given column setting. Kept in the shape the renderer has always used
 * rather than simplified to `columns`, because the two differ on small islands: at four nodes the
 * expression below gives two per row, and PLIĆAK has been drawn 2+2 since the first version.
 */
export function generatedPerRow(count: number, columns = GENERATED_COLUMNS): number {
  return Math.max(2, Math.min(columns + 1, Math.ceil(count / Math.max(2, Math.ceil(count / columns)))));
}

/** A fresh canvas: open sea, for the builder to carve an island out of. */
export function blankTerrain(width: number, height: number): Uint8Array {
  return new Uint8Array(width * height).fill(Terrain.Sea);
}

/* ------------------------------------------------------------------ queries over a grid */

export interface Grid {
  readonly width: number;
  readonly height: number;
  readonly tiles: ArrayLike<number>;
}

export function tileOf(grid: Grid, x: number, y: number): Terrain {
  if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) return Terrain.Fog;
  return (grid.tiles[y * grid.width + x] ?? Terrain.Fog) as Terrain;
}

export function inBounds(grid: Grid, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < grid.width && y < grid.height;
}

const STEPS: ReadonlyArray<readonly [number, number]> = [
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
];

/**
 * Every tile a player can walk to from `start`, four ways, never through solid terrain and never
 * through a tile something is standing on.
 *
 * `occupied` is the set of tile indices holding a signpost. They are passable *to* and not
 * *through*, exactly as in the game — you walk up to a marker and read it, you do not walk over
 * it — which is why the caller asks `isReached` rather than reading the visited set directly.
 */
/** Tile states in the visited map. The distinction is the whole point — see `isReached`. */
export const UNREACHED = 0;
/** The player can stand here. */
export const STANDABLE = 1;
/** Reached, but something is on it: you can walk up to it, never onto or through it. */
export const OCCUPIED = 2;

export function reachableFrom(
  grid: Grid,
  start: { x: number; y: number },
  occupied: ReadonlySet<number> = new Set(),
): Uint8Array {
  const visited = new Uint8Array(grid.width * grid.height);
  if (!inBounds(grid, start.x, start.y)) return visited;
  const startIndex = start.y * grid.width + start.x;
  if (isSolid(tileOf(grid, start.x, start.y))) return visited;

  visited[startIndex] = occupied.has(startIndex) ? OCCUPIED : STANDABLE;
  const queue = [startIndex];
  for (let head = 0; head < queue.length; head++) {
    const index = queue[head]!;
    const x = index % grid.width;
    const y = (index - x) / grid.width;
    for (const [dx, dy] of STEPS) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(grid, nx, ny)) continue;
      const next = ny * grid.width + nx;
      if (visited[next]) continue;
      if (isSolid(tileOf(grid, nx, ny))) continue;
      // Reached, but not walked through: a signpost stops the search at itself, and is recorded
      // as somewhere you cannot stand.
      if (occupied.has(next)) {
        visited[next] = OCCUPIED;
        continue;
      }
      visited[next] = STANDABLE;
      queue.push(next);
    }
  }
  return visited;
}

/**
 * Can the player get to this tile — either by standing on it, or by standing beside it and facing
 * it, which is how a signpost is read.
 *
 * The neighbour has to be a tile they can actually STAND on, and that is the subtle part. A marker
 * tile is "reached" and is not somewhere anybody can stand; counting it as a valid approach would
 * pass a second marker whose only way in is through the first — a pair of signposts in a corridor,
 * the second of which no student can ever open. That is precisely the failure this whole check
 * exists to prevent, so `OCCUPIED` neighbours do not count.
 */
export function isReached(grid: Grid, visited: ArrayLike<number>, x: number, y: number): boolean {
  if (!inBounds(grid, x, y)) return false;
  if (visited[y * grid.width + x]) return true;
  return STEPS.some(([dx, dy]) => {
    const nx = x + dx;
    const ny = y + dy;
    return inBounds(grid, nx, ny) && visited[ny * grid.width + nx] === STANDABLE;
  });
}

/**
 * The nearest tile to `from` that something can be placed on, spiralling outwards.
 *
 * Used by the builder when it has to put a new quest somewhere without being asked — a marker
 * dropped into the sea or inside a rock is worse than one a step away from where you clicked.
 */
export function nearestStandable(
  grid: Grid,
  from: { x: number; y: number },
  taken: ReadonlySet<string> = new Set(),
  maxRadius = 12,
): { x: number; y: number } | null {
  const free = (x: number, y: number): boolean =>
    inBounds(grid, x, y) && isStandable(tileOf(grid, x, y)) && !taken.has(`${x},${y}`);

  if (free(from.x, from.y)) return { x: from.x, y: from.y };
  for (let radius = 1; radius <= maxRadius; radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
        if (free(from.x + dx, from.y + dy)) return { x: from.x + dx, y: from.y + dy };
      }
    }
  }
  return null;
}
